import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { DemoApiError } from './demo-api.error';
import { DEMO_API_CONFIG } from './demo-api.tokens';
import { DemoApiQuery, DemoApiQueryValue } from './demo-api.types';

@Injectable({ providedIn: 'root' })
export class DemoApiClient {
  private readonly http = inject(HttpClient);
  private readonly config = inject(DEMO_API_CONFIG);

  get enabled(): boolean {
    return this.config.enabled;
  }

  get<T>(path: string, query?: DemoApiQuery): Observable<T> {
    return this.request<T>('GET', path, undefined, query);
  }

  post<T>(path: string, body?: unknown): Observable<T> {
    return this.request<T>('POST', path, body);
  }

  put<T>(path: string, body?: unknown): Observable<T> {
    return this.request<T>('PUT', path, body);
  }

  patch<T>(path: string, body?: unknown): Observable<T> {
    return this.request<T>('PATCH', path, body);
  }

  delete<T>(path: string): Observable<T> {
    return this.request<T>('DELETE', path);
  }

  private request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    query?: DemoApiQuery,
  ): Observable<T> {
    if (!this.config.enabled) {
      return throwError(() => DemoApiError.disabled());
    }

    return this.http
      .request<T>(method, this.resolveUrl(path), {
        body,
        context: this.config.requestContext,
        params: toHttpParams(query),
      })
      .pipe(
        catchError((error: unknown) =>
          throwError(() => DemoApiError.from(error)),
        ),
      );
  }

  private resolveUrl(path: string): string {
    const baseUrl = this.config.baseUrl.replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');
    return normalizedPath ? `${baseUrl}/${normalizedPath}` : baseUrl;
  }
}

function toHttpParams(query?: DemoApiQuery): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    for (const item of queryValues(value)) {
      params = params.append(key, String(item));
    }
  }
  return params;
}

function queryValues(
  value: DemoApiQueryValue,
): readonly (string | number | boolean)[] {
  if (value === null || value === undefined || value === '') return [];
  if (Array.isArray(value)) {
    return value as readonly (string | number | boolean)[];
  }
  return [value as string | number | boolean];
}
